import { ArrowRight, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useFormat } from '@/i18n/useFormat'
import type { CaseLeg } from '@/types/domain'

import { LegPlace } from './LegPlace'

/** The summary reads the same served point and due date as the full chain.
 * An automatic arrival remains visibly different from an operator's report. */
export function RouteOverview({ leg }: { leg: CaseLeg }) {
  const { t } = useTranslation()
  const format = useFormat()
  const points = leg.points ?? []
  const currentIndex = points.findIndex((point) => point.id === leg.currentPointId)
  const current = currentIndex >= 0 ? points[currentIndex] : undefined
  const next = currentIndex >= 0 ? points[currentIndex + 1] : undefined
  const date = (value: string) =>
    new Intl.DateTimeFormat(format.locale, { day: 'numeric', month: 'short' }).format(new Date(value))

  if (!current) {
    return <p className="text-text-muted text-sm">{t('cases.legPage.positionUnknown')}</p>
  }

  return (
    <>
      <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(40px,0.6fr)_minmax(0,1fr)] sm:items-center sm:px-4">
        <div aria-label={t('cases.legPage.currentPoint')} className="flex items-start gap-3">
          <MapPin size={23} className="text-brand mt-6 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-text-muted mb-1.5 text-[11px] font-medium tracking-wide uppercase">{t('cases.legPage.currentPoint')}</p>
            <p className="text-text-heading text-[19px] leading-snug font-semibold"><LegPlace name={current.name} country={current.country} /></p>
            {current.reachedAt ? (
              <p className="text-text-muted mt-1 text-[13px]">
                {date(current.reachedAt)} · {current.reachedSource === 'auto' ? t('cases.legPage.pointReachedAuto') : t('cases.legPage.pointReachedFact')}
              </p>
            ) : null}
          </div>
        </div>
        <div className="text-text-muted hidden items-center sm:flex" aria-hidden>
          <span className="border-border-default flex-1 border-t border-dashed" />
          <ArrowRight size={19} className="mx-2 shrink-0" />
          <span className="border-border-default flex-1 border-t border-dashed" />
        </div>
        <div aria-label={t('cases.legPage.nextPoint')} className="flex items-start gap-3 sm:pl-4">
          <MapPin size={23} className="text-text-muted mt-6 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-text-muted mb-1.5 text-[11px] font-medium tracking-wide uppercase">{t('cases.legPage.nextPoint')}</p>
            <p className="text-text-heading text-[19px] leading-snug font-semibold">
              {next ? <LegPlace name={next.name} country={next.country} /> : t('cases.legPage.routeEnd')}
            </p>
            {next && leg.nextPointDueAt ? <p className="text-text-muted mt-1 text-[13px]">{t('cases.legPage.pointExpected', { date: date(leg.nextPointDueAt) })}</p> : null}
          </div>
        </div>
      </div>
      <p className="text-text-muted mt-5 text-xs">
        {current.reachedSource === 'auto' ? t('cases.legPage.positionAutomatic') : current.reachedAt ? t('cases.legPage.positionReported') : t('cases.legPage.positionUnknown')}
      </p>
    </>
  )
}
